import TurndownService from 'turndown';

// Enhanced HTML to Markdown converter specifically designed for TipTap editor content
export class RobustHtmlToMarkdownConverter {
  private turndownService: TurndownService;

  constructor() {
    this.turndownService = new TurndownService({
      headingStyle: 'atx',
      codeBlockStyle: 'fenced',
      fence: '```',
      emDelimiter: '*',
      strongDelimiter: '**',
      linkStyle: 'inlined',
      linkReferenceStyle: 'full',
      preformattedCode: false,
    });

    this.setupCustomRules();
  }

  private setupCustomRules() {
    // Enhanced table support with proper formatting
    this.turndownService.addRule('table', {
      filter: 'table',
      replacement: (content, node) => {
        const table = node as HTMLTableElement;
        const rows = Array.from(table.querySelectorAll('tr'));

        if (rows.length === 0) return '';

        const markdownRows = rows.map((row, rowIndex) => {
          const cells = Array.from(row.children).map((cell) => {
            // Handle nested formatting within cells
            const cellContent = this.turndownService.turndown(cell.innerHTML).replace(/\n/g, ' ').trim();
            return cellContent || ' ';
          });

          return '| ' + cells.join(' | ') + ' |';
        });

        // Add header separator for the first row (assuming it's a header)
        if (markdownRows.length > 0) {
          const firstRow = markdownRows[0];
          const cellCount = (firstRow.match(/\|/g) || []).length - 1;
          const separator = '| ' + Array(cellCount).fill('---').join(' | ') + ' |';

          return [markdownRows[0], separator, ...markdownRows.slice(1)].join('\n');
        }

        return markdownRows.join('\n');
      },
    });

    // Handle TipTap's text alignment
    this.turndownService.addRule('textAlign', {
      filter: (node) => {
        const element = node as HTMLElement;
        return !!(element.style && element.style.textAlign && element.style.textAlign !== 'left');
      },
      replacement: (content, node) => {
        const element = node as HTMLElement;
        const alignment = element.style.textAlign;

        // Add alignment indicators (this is custom and may need adjustment based on your needs)
        switch (alignment) {
          case 'center':
            return `<div align="center">\n\n${content}\n\n</div>`;
          case 'right':
            return `<div align="right">\n\n${content}\n\n</div>`;
          case 'justify':
            return `<div align="justify">\n\n${content}\n\n</div>`;
          default:
            return content;
        }
      },
    });

    // Handle underline (not standard markdown, so preserve as HTML)
    this.turndownService.addRule('underline', {
      filter: 'u',
      replacement: (content) => `<u>${content}</u>`,
    });

    // Handle highlight/mark elements
    this.turndownService.addRule('highlight', {
      filter: 'mark',
      replacement: (content) => `==${content}==`, // Using highlight syntax
    });

    // Handle subscript and superscript
    this.turndownService.addRule('subscript', {
      filter: 'sub',
      replacement: (content) => `~${content}~`,
    });

    this.turndownService.addRule('superscript', {
      filter: 'sup',
      replacement: (content) => `^${content}^`,
    });

    // Handle TipTap's paragraph with specific classes or styles
    this.turndownService.addRule('paragraphWithStyle', {
      filter: (node) => {
        const element = node as HTMLElement;
        return element.tagName === 'P' && !!(element.className || element.style.cssText);
      },
      replacement: (content, node) => {
        const element = node as HTMLElement;

        // Check for text alignment
        if (element.style.textAlign && element.style.textAlign !== 'left') {
          const alignment = element.style.textAlign;
          switch (alignment) {
            case 'center':
              return `<p align="center">${content}</p>\n\n`;
            case 'right':
              return `<p align="right">${content}</p>\n\n`;
            case 'justify':
              return `<p align="justify">${content}</p>\n\n`;
          }
        }

        return content ? `${content}\n\n` : '';
      },
    });

    // Handle empty paragraphs (common in TipTap)
    this.turndownService.addRule('emptyParagraph', {
      filter: (node) => {
        return node.nodeName === 'P' && (!node.textContent || node.textContent.trim() === '');
      },
      replacement: () => '\n',
    });
  }

  /**
   * Convert HTML from TipTap editor to Markdown
   */
  public convertHtmlToMarkdown(html: string): string {
    if (!html || html.trim() === '') return '';

    try {
      // Clean up the HTML first
      const cleanedHtml = this.preprocessHtml(html);

      // Convert to markdown
      const markdown = this.turndownService.turndown(cleanedHtml);

      // Post-process the markdown
      return this.postprocessMarkdown(markdown);
    } catch (error) {
      console.error('Error converting HTML to Markdown:', error);
      return html; // Return original HTML if conversion fails
    }
  }

  /**
   * Preprocess HTML to handle TipTap-specific quirks
   */
  private preprocessHtml(html: string): string {
    return (html
      // Remove TipTap's data attributes that aren't needed in markdown
      .replace(/\s*data-[\w-]+="[^"]*"/g, '')
      // Clean up excessive whitespace
      .replace(/\s+/g, ' ')
      // Handle TipTap's empty paragraphs with br tags
      .replace(/<p><br><\/p>/g, '\n')
      // Handle multiple consecutive br tags
      .replace(/(<br\s*\/?>){2,}/g, '\n\n'));
  }

  /**
   * Post-process markdown to clean up formatting
   */
  private postprocessMarkdown(markdown: string): string {
    return (markdown
      // Clean up excessive line breaks
      .replace(/\n{3,}/g, '\n\n')
      // Remove trailing whitespace from lines
      .replace(/[ \t]+$/gm, '')
      // Ensure tables have proper spacing
      .replace(/(\|.*\|)\n(\|.*\|)/g, '$1\n$2')
      // Clean up list formatting
      .replace(/^(\s*[-*+])\s+/gm, '$1 ')
      // Trim final result
      .trim());
  }

  /**
   * Get the underlying TurndownService instance for custom configuration
   */
  public getTurndownService(): TurndownService {
    return this.turndownService;
  }
}

// Export a default instance
export const robustHtmlToMarkdownConverter = new RobustHtmlToMarkdownConverter();

// Export the main conversion function
export const convertTipTapHtmlToMarkdown = (html: string): string => {
  return robustHtmlToMarkdownConverter.convertHtmlToMarkdown(html);
};

// Export for backward compatibility with existing code
export const htmlToMarkdown = convertTipTapHtmlToMarkdown;
